import React, { useState } from "react";
import { fetchWrapper } from "utils/apiFetch";

// ─── Service Definitions ───────────────────────────────────────────────────
const KYC_SERVICES = [
  {
    id: "PANCARD",
    label: "PAN Card",
    icon: "ph-identification-card",
    color: "#f97316",
    desc: "Verify PAN with name & DOB match",
    fields: [
      { key: "pan_number", label: "PAN Number", placeholder: "ABCDE1234F", type: "text" },
      { key: "name", label: "Name (optional)", placeholder: "John Doe", type: "text", optional: true },
      { key: "dob", label: "Date of Birth (optional)", placeholder: "1993-06-30", type: "text", optional: true },
    ],
  },
  {
    id: "AADHAAR",
    label: "Aadhaar OTP",
    icon: "ph-fingerprint",
    color: "#8b5cf6",
    desc: "Two-step Aadhaar OTP verification",
    fields: [
      { key: "aadhaar_number", label: "Aadhaar Number", placeholder: "1234 5678 9012", type: "text" },
      { key: "step", label: "Step", type: "select", options: ["send_otp", "verify_otp"] },
      { key: "otp", label: "OTP", placeholder: "6-digit OTP", type: "text", conditional: "step=verify_otp" },
      { key: "ref_id", label: "Ref ID (from send_otp)", placeholder: "Reference ID", type: "text", conditional: "step=verify_otp" },
    ],
  },
  {
    id: "BANK_ACCOUNT",
    label: "Bank Account",
    icon: "ph-bank",
    color: "#10b981",
    desc: "Verify bank account with IFSC",
    fields: [
      { key: "account_number", label: "Account Number", placeholder: "1234567890", type: "text" },
      { key: "ifsc", label: "IFSC Code", placeholder: "SBIN0001234", type: "text" },
      { key: "name", label: "Account Holder Name (optional)", placeholder: "John Doe", type: "text", optional: true },
    ],
  },
  {
    id: "GST",
    label: "GST / GSTIN",
    icon: "ph-receipt",
    color: "#06b6d4",
    desc: "Verify GSTIN & fetch business details",
    fields: [
      { key: "gstin", label: "GSTIN", placeholder: "29AAICP2912R1ZR", type: "text" },
      { key: "business_name", label: "Business Name (optional)", placeholder: "Company Pvt Ltd", type: "text", optional: true },
    ],
  },
  {
    id: "PAN_GSTIN",
    label: "PAN → GSTIN",
    icon: "ph-arrows-left-right",
    color: "#f59e0b",
    desc: "Fetch all GSTINs linked to a PAN",
    fields: [
      { key: "pan_number", label: "PAN Number", placeholder: "ABCDE1234F", type: "text" },
    ],
  },
  {
    id: "PAN_UDYAM",
    label: "PAN → Udyam",
    icon: "ph-factory",
    color: "#ec4899",
    desc: "Fetch Udyam registration from PAN",
    fields: [
      { key: "pan_number", label: "PAN Number", placeholder: "ABCDE1234F", type: "text" },
    ],
  },
  {
    id: "UDYAM",
    label: "Udyam",
    icon: "ph-buildings",
    color: "#6366f1",
    desc: "Verify Udyam registration number",
    fields: [
      { key: "udyam_number", label: "Udyam Number", placeholder: "UDYAM-UP-43-1234567", type: "text" },
    ],
  },
  {
    id: "CIN",
    label: "CIN",
    icon: "ph-briefcase",
    color: "#14b8a6",
    desc: "Company Identification Number lookup",
    fields: [
      { key: "cin", label: "CIN", placeholder: "U72900KA2015PTC082988", type: "text" },
    ],
  },
  {
    id: "DRIVING LICENCE",
    label: "Driving Licence",
    icon: "ph-steering-wheel",
    color: "#ef4444",
    desc: "Verify driving licence details",
    fields: [
      { key: "dl_number", label: "DL Number", placeholder: "KA0120198900984", type: "text" },
      { key: "dob", label: "Date of Birth", placeholder: "1994-08-05", type: "text" },
    ],
  },
  {
    id: "VOTERID",
    label: "Voter ID",
    icon: "ph-check-square",
    color: "#84cc16",
    desc: "Verify voter ID / EPIC number",
    fields: [
      { key: "epic_number", label: "EPIC Number", placeholder: "UAI4574761", type: "text" },
      { key: "name", label: "Name (optional)", placeholder: "John Doe", type: "text", optional: true },
    ],
  },
  {
    id: "PASSPORT",
    label: "Passport",
    icon: "ph-book-open",
    color: "#0ea5e9",
    desc: "Verify passport file number",
    fields: [
      { key: "name", label: "Full Name", placeholder: "John Doe", type: "text" },
      { key: "file_number", label: "File Number", placeholder: "PA1079341954215", type: "text" },
      { key: "dob", label: "Date of Birth", placeholder: "1998-12-31", type: "text" },
    ],
  },
  {
    id: "VEHICLE_RC",
    label: "Vehicle RC",
    icon: "ph-car",
    color: "#a855f7",
    desc: "Verify vehicle registration certificate",
    fields: [
      { key: "vehicle_number", label: "Vehicle Number", placeholder: "PY01MW8769", type: "text" },
    ],
  },
  {
    id: "MOBILE_PENNYDROP",
    label: "Mobile Penny Drop",
    icon: "ph-device-mobile",
    color: "#f43f5e",
    desc: "Verify UPI VPA via mobile penny drop",
    fields: [
      { key: "mobile_number", label: "Mobile Number", placeholder: "9999999999", type: "text" },
      { key: "name", label: "Name (optional)", placeholder: "John Doe", type: "text", optional: true },
    ],
  },
  {
    id: "FACE_MATCH",
    label: "Face Match",
    icon: "ph-scan-smiley",
    color: "#fb923c",
    desc: "Match two face images (upload files)",
    fields: [
      { key: "first_image", label: "First Image", type: "file" },
      { key: "second_image", label: "Second Image", type: "file" },
      { key: "threshold", label: "Match Threshold (default 0.75)", placeholder: "0.75", type: "text", optional: true },
    ],
    isMultipart: true,
  },
  {
    id: "AADHAAR_MASKING",
    label: "Aadhaar Masking",
    icon: "ph-eye-slash",
    color: "#64748b",
    desc: "Mask Aadhaar number in uploaded image",
    fields: [
      { key: "image", label: "Aadhaar Image", type: "file" },
    ],
    isMultipart: true,
  },
];

// ─── Helpers ───────────────────────────────────────────────────────────────
const showToast = (message, type = "success") => {
  const el = document.createElement("div");
  el.className = `alert alert-${type} alert-dismissible position-fixed top-0 end-0 m-3 shadow-lg`;
  el.style.cssText = "z-index:9999;min-width:300px;border-radius:12px;";
  el.innerHTML = `<div class="d-flex align-items-center gap-2">
    <i class="ph-duotone ${type === "success" ? "ph-check-circle" : "ph-x-circle"}" style="font-size:1.3rem"></i>
    <span class="flex-grow-1">${message}</span>
    <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
  </div>`;
  document.body.appendChild(el);
  setTimeout(() => el?.remove(), 4000);
};

const renderValue = (val, depth = 0) => {
  if (val === null || val === undefined) return <span className="text-muted">—</span>;
  if (typeof val === "boolean") return <span className={val ? "badge bg-success" : "badge bg-danger"}>{val ? "Yes" : "No"}</span>;
  if (Array.isArray(val)) {
    if (val.length === 0) return <span className="text-muted">[]</span>;
    return (
      <div className="ms-2">
        {val.map((item, i) => (
          <div key={i} className="border-start ps-2 mb-1" style={{ borderColor: "#e2e8f0" }}>
            {typeof item === "object" ? renderValue(item, depth + 1) : <span>{String(item)}</span>}
          </div>
        ))}
      </div>
    );
  }
  if (typeof val === "object") {
    return (
      <div className="ms-2">
        {Object.entries(val).map(([k, v]) => (
          <div key={k} className="d-flex gap-2 mb-1 flex-wrap">
            <span className="text-muted small fw-semibold" style={{ minWidth: 140 }}>{k}</span>
            <span>{renderValue(v, depth + 1)}</span>
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(val)}</span>;
};

// ─── Result Modal ──────────────────────────────────────────────────────────
function ResultPanel({ result, onClose }) {
  if (!result) return null;
  const isSuccess = result.status === true;
  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 5000,
        background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 20, width: "100%", maxWidth: 640,
          maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column",
          boxShadow: "0 25px 60px rgba(0,0,0,0.25)",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 24px",
          background: isSuccess ? "linear-gradient(135deg,#10b981,#059669)" : "linear-gradient(135deg,#ef4444,#dc2626)",
          color: "#fff",
        }}>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <i className={`ph-duotone ${isSuccess ? "ph-check-circle" : "ph-x-circle"}`} style={{ fontSize: 32 }} />
              <div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {isSuccess ? "Verification Successful" : "Verification Failed"}
                </div>
                <div style={{ opacity: 0.85, fontSize: 13 }}>{result.message}</div>
              </div>
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.2)", border: "none", borderRadius: 8, color: "#fff", padding: "6px 10px", cursor: "pointer" }}>
              <i className="ph-duotone ph-x" />
            </button>
          </div>
          {result.reference_num && (
            <div style={{ marginTop: 12, background: "rgba(255,255,255,0.15)", borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
              <span style={{ opacity: 0.8 }}>Reference: </span>
              <strong>{result.reference_num}</strong>
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {result.data && Object.keys(result.data).length > 0 ? (
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
                Verification Data
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {Object.entries(result.data).map(([k, v]) => (
                  <div key={k} style={{
                    display: "flex", gap: 12, padding: "10px 14px",
                    background: "#f8fafc", borderRadius: 10, flexWrap: "wrap",
                    border: "1px solid #e2e8f0",
                  }}>
                    <span style={{ color: "#64748b", fontSize: 12, fontWeight: 600, minWidth: 160, flexShrink: 0 }}>
                      {k.replace(/_/g, " ").toUpperCase()}
                    </span>
                    <span style={{ fontSize: 13, color: "#1e293b", flex: 1 }}>{renderValue(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4 text-muted">
              <i className="ph-duotone ph-info" style={{ fontSize: 32 }} />
              <p className="mt-2">No additional data returned.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Service Form ──────────────────────────────────────────────────────────
function ServiceForm({ service, userId, onResult }) {
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState("");
  const [amount, setAmount] = useState("0");

  const handleChange = (key, value) => setFormData(p => ({ ...p, [key]: value }));
  const handleFile = (key, file) => setFiles(p => ({ ...p, [key]: file }));

  const isVisible = (field) => {
    if (!field.conditional) return true;
    const [k, v] = field.conditional.split("=");
    return formData[k] === v;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let body, headers = {};

      if (service.isMultipart) {
        const fd = new FormData();
        fd.append("kyc_type", service.id);
        fd.append("user_id", userId);
        fd.append("pin", pin);
        fd.append("amount", amount);
        Object.entries(formData).forEach(([k, v]) => fd.append(k, v));
        Object.entries(files).forEach(([k, f]) => fd.append(k, f));
        body = fd;
      } else {
        headers["Content-Type"] = "application/json";
        body = JSON.stringify({ kyc_type: service.id, user_id: userId, pin, amount, ...formData });
      }

      const res = await fetchWrapper("https://api.mokshiq.in/kyc_verification_request", {
        method: "POST",
        headers,
        body,
      });

      onResult(res);
      if (res.status) {
        showToast(res.message || "Verification successful!", "success");
      } else {
        showToast(res.message || "Verification failed.", "danger");
      }
    } catch (err) {
      showToast("Something went wrong!", "danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="row g-3">
        {/* Dynamic Fields */}
        {service.fields.map((field) => {
          if (!isVisible(field)) return null;
          return (
            <div key={field.key} className="col-12">
              <label className="form-label fw-semibold" style={{ fontSize: 13, color: "#475569" }}>
                {field.label}
                {field.optional && <span className="text-muted fw-normal ms-1">(optional)</span>}
              </label>
              {field.type === "select" ? (
                <select
                  className="form-select"
                  value={formData[field.key] || field.options[0]}
                  onChange={e => handleChange(field.key, e.target.value)}
                  style={{ borderRadius: 10 }}
                >
                  {field.options.map(o => (
                    <option key={o} value={o}>{o.replace(/_/g, " ")}</option>
                  ))}
                </select>
              ) : field.type === "file" ? (
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={e => handleFile(field.key, e.target.files[0])}
                  style={{ borderRadius: 10 }}
                  required={!field.optional}
                />
              ) : (
                <input
                  type={field.type}
                  className="form-control"
                  placeholder={field.placeholder}
                  value={formData[field.key] || ""}
                  onChange={e => handleChange(field.key, e.target.value)}
                  style={{ borderRadius: 10 }}
                  required={!field.optional}
                />
              )}
            </div>
          );
        })}

        {/* Divider */}
        <div className="col-12"><hr style={{ borderColor: "#e2e8f0" }} /></div>

        {/* Amount */}
        {/* <div className="col-6">
          <label className="form-label fw-semibold" style={{ fontSize: 13, color: "#475569" }}>Amount (₹)</label>
          <input
            type="text"
            className="form-control"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            style={{ borderRadius: 10 }}
            required
          />
        </div> */}

        {/* PIN */}
        <div className="col-6">
          <label className="form-label fw-semibold" style={{ fontSize: 13, color: "#475569" }}>Transaction PIN</label>
          <input
            type="password"
            className="form-control"
            placeholder="••••"
            value={pin}
            onChange={e => setPin(e.target.value)}
            style={{ borderRadius: 10 }}
            required
          />
        </div>

        {/* Submit */}
        <div className="col-12">
          <button
            type="submit"
            className="btn w-100"
            disabled={loading}
            style={{
              background: service.color,
              color: "#fff",
              borderRadius: 12,
              padding: "12px",
              fontWeight: 700,
              fontSize: 15,
              border: "none",
              boxShadow: `0 4px 15px ${service.color}44`,
              transition: "opacity 0.2s",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? (
              <span className="d-flex align-items-center justify-content-center gap-2">
                <span className="spinner-border spinner-border-sm" />
                Verifying...
              </span>
            ) : (
              <span className="d-flex align-items-center justify-content-center gap-2">
                <i className={`ph-duotone ${service.icon}`} />
                Verify {service.label}
              </span>
            )}
          </button>
        </div>
      </div>
    </form>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
function KYCVerification() {
  const [selectedService, setSelectedService] = useState(null);
  const [result, setResult] = useState(null);
  const [search, setSearch] = useState("");
  const userId = localStorage.getItem("userId") || "3";

  const filtered = KYC_SERVICES.filter(s =>
    s.label.toLowerCase().includes(search.toLowerCase()) ||
    s.desc.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <main style={{ fontFamily: "'DM Sans', sans-serif", minHeight: "100vh", background: "#f1f5f9" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />

      {/* ── Header ── */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "16px 24px" }}>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div>
            <h4 className="mb-0 fw-bold" style={{ color: "#0f172a", fontSize: 22 }}>
              <i className="ph-duotone ph-shield-check me-2" style={{ color: "#6366f1" }} />
              Verification Suite
            </h4>
            <ul className="app-line-breadcrumbs mb-0 mt-1">
              <li><a className="f-s-14 f-w-500" href="#"><span><i className="ph-duotone ph-briefcase-metal f-s-16" /> Services</span></a></li>
              <li className="active"><a className="f-s-14 f-w-500" href="#">KYC Verification</a></li>
            </ul>
          </div>
          <div style={{ position: "relative" }}>
            <i className="ph-duotone ph-magnifying-glass" style={{
              position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
              color: "#94a3b8", fontSize: 16,
            }} />
            <input
              type="text"
              placeholder="Search services..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                padding: "9px 16px 9px 38px",
                border: "1.5px solid #e2e8f0",
                borderRadius: 12,
                fontSize: 14,
                width: 240,
                outline: "none",
                background: "#f8fafc",
                color: "#1e293b",
              }}
            />
          </div>
        </div>
      </div>

      <div className="container-fluid py-4 px-3 px-md-4">
        <div className="row g-4">

          {/* ── Left: Service Grid ── */}
          <div className={selectedService ? "col-lg-5 col-xl-4" : "col-12"}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: selectedService
                  ? "repeat(auto-fill, minmax(160px, 1fr))"
                  : "repeat(auto-fill, minmax(200px, 1fr))",
                gap: 16,
              }}
            >
              {filtered.map(service => {
                const isActive = selectedService?.id === service.id;
                return (
                  <div
                    key={service.id}
                    onClick={() => { setSelectedService(service); setResult(null); }}
                    style={{
                      background: isActive ? service.color : "#fff",
                      border: `2px solid ${isActive ? service.color : "#e2e8f0"}`,
                      borderRadius: 16,
                      padding: selectedService ? "16px 14px" : "20px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      boxShadow: isActive ? `0 8px 25px ${service.color}33` : "0 1px 4px rgba(0,0,0,0.06)",
                      transform: isActive ? "scale(1.02)" : "scale(1)",
                    }}
                  >
                    <div style={{
                      width: 44, height: 44, borderRadius: 12,
                      background: isActive ? "rgba(255,255,255,0.25)" : `${service.color}15`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      marginBottom: 12,
                    }}>
                      <i className={`ph-duotone ${service.icon}`} style={{
                        fontSize: 22, color: isActive ? "#fff" : service.color,
                      }} />
                    </div>
                    <div style={{
                      fontWeight: 700, fontSize: 14,
                      color: isActive ? "#fff" : "#1e293b",
                      marginBottom: 4,
                    }}>
                      {service.label}
                    </div>
                    {!selectedService && (
                      <div style={{ fontSize: 12, color: isActive ? "rgba(255,255,255,0.8)" : "#94a3b8", lineHeight: 1.4 }}>
                        {service.desc}
                      </div>
                    )}
                    {selectedService && (
                      <div style={{ fontSize: 11, color: isActive ? "rgba(255,255,255,0.8)" : "#94a3b8" }}>
                        {isActive ? "Selected" : "Tap to use"}
                      </div>
                    )}
                  </div>
                );
              })}

              {filtered.length === 0 && (
                <div className="text-center py-5 text-muted" style={{ gridColumn: "1/-1" }}>
                  <i className="ph-duotone ph-magnifying-glass" style={{ fontSize: 40 }} />
                  <p className="mt-2">No services match your search.</p>
                </div>
              )}
            </div>
          </div>

          {/* ── Right: Form Panel ── */}
          {selectedService && (
            <div className="col-lg-7 col-xl-8">
              <div style={{
                background: "#fff", borderRadius: 20,
                border: "1.5px solid #e2e8f0",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                overflow: "hidden",
                position: "sticky", top: 16,
              }}>
                {/* Form Header */}
                <div style={{
                  padding: "20px 24px",
                  borderBottom: "1px solid #f1f5f9",
                  background: `linear-gradient(135deg, ${selectedService.color}10, ${selectedService.color}05)`,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <div className="d-flex align-items-center gap-3">
                    <div style={{
                      width: 48, height: 48, borderRadius: 14,
                      background: `${selectedService.color}18`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <i className={`ph-duotone ${selectedService.icon}`} style={{ fontSize: 24, color: selectedService.color }} />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 17, color: "#0f172a" }}>{selectedService.label}</div>
                      <div style={{ fontSize: 13, color: "#64748b" }}>{selectedService.desc}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => { setSelectedService(null); setResult(null); }}
                    style={{
                      background: "#f1f5f9", border: "none", borderRadius: 10,
                      padding: "8px 12px", cursor: "pointer", color: "#64748b",
                    }}
                  >
                    <i className="ph-duotone ph-x" />
                  </button>
                </div>

                {/* Form Body */}
                <div style={{ padding: 24 }}>
                  <ServiceForm
                    service={selectedService}
                    userId={userId}
                    onResult={setResult}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Empty State */}
        {!selectedService && (
          <div className="text-center mt-5 pt-3" style={{ color: "#94a3b8" }}>
            <i className="ph-duotone ph-cursor-click" style={{ fontSize: 48, color: "#cbd5e1" }} />
            <p className="mt-2 fw-medium" style={{ fontSize: 15 }}>Select a verification service above to get started</p>
          </div>
        )}
      </div>

      {/* ── Result Modal ── */}
      {result && <ResultPanel result={result} onClose={() => setResult(null)} />}
    </main>
  );
}

export default KYCVerification;